import get from 'lodash.get';
import { trackEcommerce, EcomProduct } from 'dibs-tracking';
import { defaultLocale } from 'dibs-intl/exports/constants';
import { pageTypeConstants as pageTypes } from '../../../constants/pageTypeConstants';
import { ECOMMERCE_ATTRIBUTES } from '../../../constants/attributesConstants';
import { PAGE_TYPE } from 'dibs-constants/exports/pageTypes';

import {
    getIsFreeShipping,
    UserRegions,
} from 'dibs-search-product-tile/exports/freeShippingHelpers';

const LIST_TRACKING_SEARCH = 'search results listing';
const LIST_TRACKING_BROWSE = 'browse page listing';
const LIST_TRACKING_ATTRIBUTE = 'attribute listing';
const LIST_TRACKING_STOREFRONT = 'storefront listing';
const LIST_TRACKING_BUY_PAGE = 'buy page';
const LIST_TRACKING_SEMANTIC = 'semantic result listing';

import { ArrayElement } from 'dibs-ts-utils/exports/ArrayElement';

import { SbSharedThinBrowseItemTrackingProvider_items as ThinBrowseProviderItems } from '../../../finding/SbSharedThinBrowseItemTrackingProvider/__generated__/SbSharedThinBrowseItemTrackingProvider_items.graphql';

import SV from 'server-vars';

export const NUMBER_OF_PRODUCT_IMPRESSIONS = 20;

type PageTypesConstantType = typeof pageTypes;
type PageType = PageTypesConstantType[keyof PageTypesConstantType];
type SearchTermType = string | null;
type ItemsType = ThinBrowseProviderItems;
type ItemType = ArrayElement<ItemsType>;
type WrappedItemType = { item: ItemType };

const getListType = (pageType: PageType, searchCorrectionTerm: string): string => {
    const { SEARCH, BROWSE, DEALER } = pageTypes;

    if (searchCorrectionTerm && pageType !== SEARCH) {
        return LIST_TRACKING_SEMANTIC;
    } else if (pageType === SEARCH) {
        return LIST_TRACKING_SEARCH;
    } else if (pageType === BROWSE) {
        return LIST_TRACKING_BROWSE;
    } else if (ECOMMERCE_ATTRIBUTES.includes(pageType)) {
        return LIST_TRACKING_ATTRIBUTE;
    } else if (pageType === DEALER) {
        return LIST_TRACKING_STOREFRONT;
    } else if (pageType === PAGE_TYPE.BUY) {
        return LIST_TRACKING_BUY_PAGE;
    } else {
        return `${pageType} listing`;
    }
};

const getProductData = ({
    index,
    item,
    pageType,
    searchTerm,
    searchCorrectionTerm = '',
    userRegions,
}: {
    index: number;
    item: ItemType;
    pageType: PageType;
    searchTerm: SearchTermType;
    searchCorrectionTerm?: string;
    userRegions: UserRegions;
}): EcomProduct => {
    const {
        serviceId,
        title,
        seller,
        displayPriceTracking,
        categoryCode,
        browseUrl,
        contemporaryTrackingString,
    } = item;

    let priceType = 'list price';

    const primaryDiplayPriceTracking = displayPriceTracking?.[0];
    const convertedAmountList = primaryDiplayPriceTracking?.convertedAmountList;
    const type = primaryDiplayPriceTracking?.type;

    if (type === 'WEEKLY_SALE') {
        priceType = 'saturday sale';
    } else if (type === 'NET') {
        priceType = 'net price';
    }

    const usdPrice = (convertedAmountList || []).find(
        convertedAmount => convertedAmount?.currency === 'USD'
    );
    const price = usdPrice?.amount || 0;

    const baseObject = {
        id: serviceId,
        name: SV.get('locale') === defaultLocale ? title : 'n/a',
        position: index + 1, // 1-indexed
        brand: get(seller, 'serviceId'),
        category: browseUrl,
        categoryCode,
        dimension76: priceType,
        dimension83: contemporaryTrackingString,
        dimension106: getIsFreeShipping({ item, userRegions }) ? 'Y' : 'N',
        price,
        list: getListType(pageType, searchCorrectionTerm),
    };

    return Object.assign(
        baseObject,
        (searchTerm && pageType === pageTypes.SEARCH) || searchCorrectionTerm
            ? {
                  dimension98: searchCorrectionTerm || searchTerm,
                  searchCategoryClassification:
                      pageType === pageTypes.SEARCH && searchCorrectionTerm ? 'Y' : 'N',
              }
            : {}
    );
};

export const trackEcommerceProductImpressions = ({
    items,
    pageType,
    searchTerm,
    startIndex = 0,
    searchCorrectionTerm,
    userRegions,
}: {
    items: { node: ItemType | WrappedItemType }[];
    pageType: PageType;
    searchTerm: SearchTermType;
    startIndex: number;
    searchCorrectionTerm?: string;
    userRegions: UserRegions;
}): void => {
    const itemProductData = items
        .slice(startIndex, startIndex + NUMBER_OF_PRODUCT_IMPRESSIONS)
        .map(({ node }, index) =>
            getProductData({
                pageType,
                searchTerm,
                searchCorrectionTerm,
                item: (node as WrappedItemType)?.item || node,
                index: startIndex + index,
                userRegions,
            })
        );

    trackEcommerce({
        type: 'productImpression',
        products: itemProductData,
    });
};

export const trackEcommerceProductClick = ({
    item,
    index,
    pageType,
    searchTerm,
    searchCorrectionTerm = '',
    userRegions,
}: {
    index: number;
    item: ItemType;
    pageType: string;
    searchTerm: SearchTermType;
    searchCorrectionTerm?: string;
    userRegions: UserRegions;
}): void => {
    trackEcommerce({
        type: 'productClick',
        actionField: {
            list: getListType(pageType, searchCorrectionTerm),
            itemId: item?.serviceId,
        },
        products: [
            getProductData({
                item,
                pageType,
                index,
                searchTerm,
                searchCorrectionTerm,
                userRegions,
            }),
        ],
    });
};

// TODO: FINDING-12480 make sure itemFragment below contains all
// information for every function that uses item in this file and
// remove fields from individual components

// export const itemFragment = graphql`
//     fragment ecommerceTracking_item on Item {
//         title
//         serviceId
//         categoryPath
//         contemporaryTrackingString
//         vertical
//         seller {
//             serviceId
//         }
//         displayInfo(buyerId: $userId) @include(if: $isClient) {
//             pricing {
//                 showNet
//                 showSale
//             }
//         }
//         pricing {
//             retailPrice {
//                 amount
//             }
//             netPrice {
//                 amount
//             }
//             salePrice {
//                 amount
//             }
//         }
//         shipmentQuotes {
//             totalAmount {
//                 amount
//             }
//             serviceMethod {
//                 region {
//                     regionName
//                 }
//             }
//         }
//     }
// `;
